import React, { useState } from 'react';
import type { PtDirectoryPatient } from '../../../patients-list/types';
import { ptFormatMoney, ptRollupHealth } from '../../../patients-list/utils';
import { Icon } from '../../Icon/Icon';
import { Pill } from '../../Pill/Pill';
import { PatientDirectoryCard } from './PatientDirectoryCard';

export interface PatientsDirectorySectionProps {
  eyebrow: string;
  title: string;
  patients: PtDirectoryPatient[];
  minWidth: number;
  showPhotos?: boolean;
  defaultOpen?: boolean;
  onSelectPatient?: (patient: PtDirectoryPatient) => void;
}

export function PatientsDirectorySection({
  eyebrow,
  title,
  patients,
  minWidth,
  showPhotos = true,
  defaultOpen = true,
  onSelectPatient,
}: PatientsDirectorySectionProps) {
  const [open, setOpen] = useState(defaultOpen);

  if (!patients.length) return null;

  const critical = patients.filter((p) => ptRollupHealth(p) === 'critical').length;
  const production = patients.reduce((s, p) => s + (p.todayValue || 0), 0);

  return (
    <section className="max-w-[1640px] px-[30px] pb-8">
      <button
        type="button"
        className="pt-section-toggle"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
      >
        <span className={open ? 'pt-section-chevron' : 'pt-section-chevron pt-section-chevron--closed'}>
          <Icon kind="down" size={16} stroke={2} />
        </span>
        <span className="whitespace-nowrap text-[10.5px] font-bold uppercase tracking-[0.16em] text-[var(--ai-3)]">
          {eyebrow}
        </span>
        <span className="whitespace-nowrap font-[var(--font-display)] text-xl font-semibold tracking-[-0.025em] text-[var(--ink-0)]">
          {title}
        </span>
        <span className="rounded-[var(--r-pill)] bg-[var(--surface-mute)] px-[9px] py-0.5 font-[var(--font-mono)] text-xs font-semibold text-[var(--ink-2)]">
          {patients.length}
        </span>
        <span className="flex-1" />
        {critical > 0 ? (
          <Pill tone="warning" dot>
            {critical} critical
          </Pill>
        ) : null}
        {production > 0 ? (
          <Pill tone="success">
            <span className="font-[var(--font-mono)]">{ptFormatMoney(production)}</span>
            &nbsp;production
          </Pill>
        ) : null}
      </button>
      {open ? (
        <div
          className="pt-grid"
          style={{ gridTemplateColumns: `repeat(auto-fill, minmax(${minWidth}px, 1fr))` }}
        >
          {patients.map((p) => (
            <PatientDirectoryCard
              key={p.id}
              patient={p}
              showPhotos={showPhotos}
              {...(onSelectPatient ? { onSelect: onSelectPatient } : {})}
            />
          ))}
        </div>
      ) : null}
    </section>
  );
}
